import { get as lodashGet } from "lodash";
import React, { type ComponentPropsWithoutRef } from "react";
import PillarTableColumnChild from "shared/components/pillar-table/cell-content/PillarTableColumnChild";
import {
  type UnwrapArray,
  type DeepValue,
  type PathToArray,
  pillarTableColumnArrayValue,
} from "shared/components/pillar-table/columns/array/PillarTableColumnArray";
import { type PillarTableBaseColumnCellValuePathProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import {
  isPillarTableHeadingTemplate,
  renderPillarTableHeading,
} from "shared/components/pillar-table/helpers/renderPillarTableHeading";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import { type DeepKeys } from "shared/types/deepkeys";

type PillarTableColumnArrayFindProps<T> = {
  dataType?: "string" | "number" | PillarTableSafeAny;
  // findPath must be a valid deep key relative to the element type of the array at `cellValuePath`.
  findPath?: DeepKeys<UnwrapArray<DeepValue<T, PathToArray<T>>>>;
  outputPath?: DeepKeys<UnwrapArray<DeepValue<T, PathToArray<T>>>>;
  /**
   * When using an array of strings or numbers for findValue, the column will match the first matching value.
   */
  findValue: string | number | string[] | number[];
  matchOutput?: string;
  noMatchOutput?: string;
  children?: React.ReactNode;
} & Omit<PillarTableBaseColumnCellValuePathProps<T>, "cellValuePath"> & {
    cellValuePath?: PathToArray<T>;
  } & ComponentPropsWithoutRef<"div">;

type FindPillarTableArrayElementProps = {
  /** The array the column searches */
  arrayValue?: unknown[];
  /** Path on each element to compare; leave it out for an array of strings or numbers */
  findPath?: string;
  /** The value, or any of the values, to match */
  findValue: string | number | string[] | number[];
};

/** The first element of `arrayValue` that matches, the way the column cell finds it. */
export const findPillarTableArrayElement = ({
  arrayValue,
  findPath,
  findValue,
}: FindPillarTableArrayElementProps): unknown =>
  arrayValue?.find((item) => {
    if (!findPath) {
      if (typeof item !== "string" && typeof item !== "number") {
        return false;
      }

      return Array.isArray(findValue)
        ? findValue.some((val) => val === item)
        : item === findValue;
    }

    return Array.isArray(findValue)
      ? findValue.some((val) => lodashGet(item, findPath) === val)
      : lodashGet(item, findPath) === findValue;
  });

type ResolvePillarTableArrayFindHeadingProps = {
  /** The column heading, plain text or a Handlebars template */
  heading?: string;
  /** The table rows the column will show */
  rows?: unknown[];
  /** Path on each row to the array the column searches */
  cellValuePath?: string;
  /** See `FindPillarTableArrayElementProps.findPath` */
  findPath?: string;
  /** See `FindPillarTableArrayElementProps.findValue` */
  findValue: string | number | string[] | number[];
};

/**
 * A template heading renders against the element the column finds. `findValue`
 * is the same on every row, so the first row with a match speaks for the whole
 * column, e.g. `{{profile.societyUser.firstName}} Score` for one reviewer.
 */
export const resolvePillarTableArrayFindHeading = ({
  heading,
  rows = [],
  cellValuePath,
  findPath,
  findValue,
}: ResolvePillarTableArrayFindHeadingProps): string => {
  if (!isPillarTableHeadingTemplate(heading)) {
    return heading ?? "";
  }

  let foundArrayElement: unknown;
  for (const row of rows) {
    const arrayValue: unknown = Array.isArray(row)
      ? row
      : lodashGet(row, cellValuePath ?? "");
    foundArrayElement = Array.isArray(arrayValue)
      ? findPillarTableArrayElement({ arrayValue, findPath, findValue })
      : undefined;
    if (foundArrayElement !== undefined) {
      break;
    }
  }

  return renderPillarTableHeading({
    heading,
    templateData: foundArrayElement,
  });
};

export const PillarTableColumnArrayFind = <T,>({
  cellValuePath,
  children,
  dataType,
  findPath,
  findValue,
  outputPath,
  matchOutput,
  noMatchOutput,
}: PillarTableColumnArrayFindProps<T>) => {
  const rowContext = usePillarTableRowContext();
  const arrayValue = pillarTableColumnArrayValue({
    rowContext,
    cellValuePath,
  });
  const foundArrayElement = findPillarTableArrayElement({
    arrayValue,
    findPath,
    findValue,
  });

  const valuePath = outputPath ?? findPath;

  const outputValue = !findPath
    ? foundArrayElement
    : lodashGet(foundArrayElement, valuePath ?? "");

  if (matchOutput && outputValue) {
    return <>{matchOutput}</>;
  }
  if (noMatchOutput && !outputValue) {
    return <>{noMatchOutput}</>;
  }
  if (
    (dataType === "number" && typeof outputValue === "number") ||
    (dataType === "string" && typeof outputValue === "string")
  ) {
    return <>{outputValue}</>;
  }
  return (
    <PillarTableColumnChild
      rowValueOverride={foundArrayElement}
      rowIndex={rowContext?.rowIndex}
      cellValuePath={valuePath}
      columnConfig={dataType}
    >
      {children}
    </PillarTableColumnChild>
  );
};

/**
 * Renders a template heading against the element this column finds, so the
 * header can name it. A plain heading renders as written.
 */
const PillarTableColumnArrayFindHeader = <T,>({
  heading,
  cellValuePath,
  findPath,
  findValue,
}: PillarTableColumnArrayFindProps<T>) => {
  const { tableRowResults } = usePillarTableContext();

  return (
    <div className="flex flex-row items-center">
      {resolvePillarTableArrayFindHeading({
        heading,
        rows: tableRowResults,
        cellValuePath,
        findPath,
        findValue,
      })}
    </div>
  );
};

PillarTableColumnArrayFind.HeaderComponent = PillarTableColumnArrayFindHeader;
